import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Center,
  Circle,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
  useToken,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
import Color from 'color';
import { useTranslation } from 'react-i18next';
import { MdNavigateNext } from 'react-icons/md';
import { SlRefresh } from 'react-icons/sl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoaderAnimated } from '@/components/LoaderAnimated';
import { VideoCollapser } from '@/components/Video/VideoCollapser';
import { VideoPlayer } from '@/components/VideoPlayer';
import { VideoRecorder } from '@/components/VideoRecorder';
import { TechnicalProblemModal } from '@/features/coach-interview/PageCoachInterviewQuestions';
import { useMediaDeviceContext } from '@/features/media';
import {
  type UseVideoRecorderReturn,
  useVideoRecorder,
} from '@/features/video-recorder/useVideoRecorder';
import { uploadObjectToS3 } from '@/utils/s3';
import { sleep } from '@/utils/sleep';

import { FinishEarlyButton } from '../FinishEarlyButton';
import { useInterviewQuestionContext } from '../InterviewQuestion.context';
import { INTERVIEW_PATH } from '../constants';
import {
  interviewQueryKeys,
  useAnswerQuestionMutation,
  useMediaAccessMutation,
} from '../useInterviewQueries';

export const PREPARE_TIME = 5;

type VideoTypeQuestionProps = {
  adminSpecifiedPrepareTime?: number;
  isRetryable: boolean;
  candidateFullName: string;
};

export const VideoTypeQuestion: FC<VideoTypeQuestionProps> = ({
  adminSpecifiedPrepareTime,
  isRetryable,
  candidateFullName,
}) => {
  const {
    question,
    candidateId,
    interviewId,
    remainingTime,
    securityToken,
    videoIsCollapsed,
    remainingQuestions,
    setVideoIsCollapsed,
    answerBeingSubmitted,
    canProceedInterviewStep,
    setAnswerBeingSubmitted,
  } = useInterviewQuestionContext();

  const { t } = useTranslation(['interview', 'common']);
  const duration = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const technicalProblemDisclosure = useDisclosure();
  const [hasError, setHasError] = useState(false);
  const { selectedAudioDeviceId, selectedVideoDeviceId } =
    useMediaDeviceContext();

  const answerMutation = useAnswerQuestionMutation(securityToken, {
    onError() {},
  });
  const mediaAccessMutation = useMediaAccessMutation(securityToken, {
    onError() {},
  });

  const videoRecorder = useVideoRecorder({
    durationSec: remainingTime ?? 0,
    preparationSec: adminSpecifiedPrepareTime
      ? adminSpecifiedPrepareTime
      : PREPARE_TIME,
  });

  const submitVideoAnswer = async (video: Blob | File, skipped: boolean) => {
    const access = await mediaAccessMutation.mutateAsync({
      candidateId,
      contentType: video.type,
      interviewId,
      questionId: question?.id,
    });

    await uploadObjectToS3({
      blob: video,
      uploadUrl: access.signedUrl!,
      headers: {
        ...access.requestHeaders,
        'content-type': video.type,
      },
    });

    await answerMutation.mutateAsync({
      candidateId: candidateId!,
      questionId: question?.id as string,
      body: {
        videoUrl: access.finalObjectUrl,
        isSkipped: skipped,
      },
    });
  };

  const handleSubmit = async (video: Blob, skipped = false) => {
    setHasError(false);
    setAnswerBeingSubmitted(true);
    try {
      const submitAnswerTask = submitVideoAnswer(video, skipped);

      toast.promise(submitAnswerTask, {
        success: { title: t('interview:uploadSuccess') },
        error: { title: t('interview:error.toast.description.networkTimeout') },
        loading: { title: t('interview:uploadingVideo') },
      });

      await submitAnswerTask;
      setAnswerBeingSubmitted(false);
      checkQuestions();
    } catch (error) {
      setHasError(true);
      setAnswerBeingSubmitted(false);
    }
  };

  const checkQuestions = async () => {
    // If there is only one remaining question, this is the final question
    if (remainingQuestions && remainingQuestions <= 1) {
      navigate(
        INTERVIEW_PATH +
          '/submit?' +
          searchParams.toString() +
          '&interviewId=' +
          interviewId
      );
    } else {
      await queryClient.invalidateQueries({
        queryKey: interviewQueryKeys.getAllCandidatesQuestions(),
      });
      duration.current = null;
      videoRecorder.handleTryAgain();
    }
  };

  useEffect(() => {
    if (remainingTime && duration.current === null) {
      duration.current = remainingTime / 60;
      videoRecorder.resetCountdown();
    }
  }, [remainingTime, videoRecorder]);

  if (answerBeingSubmitted) return <LoaderAnimated />;

  return (
    <>
      <HStack flexDir="row-reverse" align="end">
        <Circle
          size="48px"
          bg="brand.50"
          color="brand.500"
          fontWeight="bold"
          fontSize="2xl"
          mb="26px"
        >
          {candidateFullName[0]?.toAppLocaleUpperCase()}
        </Circle>

        <VStack flexGrow={1} align="end">
          <Flex
            pos="relative"
            overflow="hidden"
            bg="gray.700"
            rounded="xl"
            w={videoIsCollapsed ? '240px' : 'calc(100% - 56px)'}
            ml="auto"
            transition="0.2s"
            borderRadius="32px 32px 4px 32px"
            border="4px solid"
            borderColor="brand.500"
            boxShadow="0px 0px 32px 0px #CFBAE999"
          >
            <Loading {...videoRecorder} />
            <VideoRecorder
              ref={videoRecorder.webcamRef}
              display={!videoRecorder.isVideoPlayMode ? undefined : 'none'}
              audioConstraints={{
                deviceId: selectedAudioDeviceId,
              }}
              webcamProps={{ allowFullScreen: true }}
              videoConstraints={{
                deviceId: selectedVideoDeviceId,
              }}
              rounded="none"
            />

            <VideoCollapser
              outsideIsCollapsed={videoIsCollapsed}
              setOutsideIsCollapsed={setVideoIsCollapsed}
            />

            <VideoPlayer
              ref={videoRecorder.videoRef}
              display={videoRecorder.isVideoPlayMode ? undefined : 'none'}
            />
          </Flex>

          <Text color="gray.300" fontSize="xs" fontWeight="medium" pr={1}>
            {candidateFullName.toAppLocaleUpperCase()}
          </Text>
        </VStack>
      </HStack>

      <Divider display={{ base: 'none', lg: 'block' }} />

      <ControlButtons
        {...videoRecorder}
        handleSubmit={async (skipped) => {
          if (videoRecorder.capturing) {
            videoRecorder.handleStopCapture();
            await sleep(1_000);
          }
          if (videoRecorder.hasTechnicalIssue) {
            return technicalProblemDisclosure.onOpen();
          }
          videoRecorder.onSubmit((video) => handleSubmit(video, skipped));
        }}
        hasError={hasError}
        durationSec={remainingTime ?? 60}
        remainingQuestions={remainingQuestions ?? 0}
        answerBeingSubmitted={answerBeingSubmitted}
        isRetryable={isRetryable}
        canProceedInterviewStep={canProceedInterviewStep}
      />

      <TechnicalProblemModal
        handleRetry={() => {
          videoRecorder.handleTryAgain();
          technicalProblemDisclosure.onClose();
        }}
        handleSubmit={() => {
          videoRecorder.onSubmit(handleSubmit);
          technicalProblemDisclosure.onClose();
        }}
        {...technicalProblemDisclosure}
      />
    </>
  );
};

interface ControlButtonsProps extends UseVideoRecorderReturn {
  durationSec: number;
  remainingQuestions: number;
  handleSubmit: (skipped: boolean) => void;
  answerBeingSubmitted: boolean;
  isRetryable: boolean;
  hasError: boolean;
  canProceedInterviewStep: boolean;
}

const ControlButtons: React.FC<ControlButtonsProps> = ({
  capturing,
  hasVideo,
  count,
  hasError,
  handleSubmit,
  durationSec,
  remainingQuestions,
  answerBeingSubmitted,
  handleTryAgain,
  handleStopCapture,
  isRetryable,
  canProceedInterviewStep,
}) => {
  const formattedCount = new Date(count * 1000).toISOString().slice(14, 19);
  const { t } = useTranslation(['interview', 'common']);
  const skipQuestionModalIsOpen = useDisclosure();

  const stage = useMemo(() => {
    if (hasError) return 'error';
    if (!capturing && hasVideo) return 'ready-to-upload';
    if (capturing) return 'recording';
    return 'ready-to-record';
  }, [capturing, hasVideo, hasError]);

  const isBtnDisabled =
    (remainingQuestions > 1 && answerBeingSubmitted) ||
    !canProceedInterviewStep ||
    stage === 'ready-to-record' ||
    (durationSec > 0 && durationSec - count <= 3);

  const tryAgainBtn = (
    <Button
      onClick={handleTryAgain}
      isDisabled={!(!capturing && hasVideo)}
      size={{ base: 'sm', lg: 'md' }}
      variant="@secondary"
      rightIcon={<SlRefresh />}
    >
      {t('interview:tryAgain')}
    </Button>
  );

  const stopRecordingBtn = (
    <Button
      onClick={handleStopCapture}
      isDisabled={!capturing}
      size={{ base: 'sm', lg: 'md' }}
      variant="@primary"
    >
      {t('interview:stopRecording')}
    </Button>
  );

  const submitBtn = (
    <Button
      onClick={() => {
        if (durationSec - count <= 7) {
          skipQuestionModalIsOpen.onOpen();
          return;
        }
        handleSubmit(false);
      }}
      flex={{ base: '1', md: 'auto' }}
      size={{ base: 'sm', lg: 'md' }}
      variant="@primary"
      rightIcon={<MdNavigateNext />}
      isDisabled={isBtnDisabled}
    >
      {remainingQuestions > 1
        ? t('common:nextQuestion')
        : t('interview:finishInterview')}
    </Button>
  );

  const uploadAgainBtn = (
    <Button
      onClick={() => handleSubmit(false)}
      size={{ base: 'sm', lg: 'md' }}
      variant="@primary"
      rightIcon={<SlRefresh />}
      isDisabled={isBtnDisabled}
    >
      {t('interview:uploadAgain')}
    </Button>
  );

  return (
    <Flex
      w={{ base: 'full', lg: 'auto' }}
      justify="space-between"
      align="center"
      pos={{ base: 'fixed', lg: 'static' }}
      zIndex="overlay"
      bottom={0}
      right={0}
      left={0}
      flexDirection={{ base: 'column', md: 'row' }}
      padding={{ base: '12px 16px', lg: 0 }}
      bg={{ base: 'white', lg: 'transparent' }}
      borderTop={{ base: '1px solid', lg: 'none' }}
      borderTopColor="gray.50"
    >
      <Flex gap="2" align="center">
        {(stage === 'recording' || stage === 'ready-to-record') && (
          <>
            <Text fontSize={{ base: 'sm', lg: 'lg' }} color="gray.500">
              {t('interview:remainingTime').toAppLocaleUpperCase()}:
            </Text>
            <CircularProgress value={(count / durationSec) * 100}>
              <CircularProgressLabel>{formattedCount}</CircularProgressLabel>
            </CircularProgress>
          </>
        )}
      </Flex>

      <SkipQuestionModal
        isOpen={skipQuestionModalIsOpen.isOpen}
        onClose={skipQuestionModalIsOpen.onClose}
        onClick={() => {
          skipQuestionModalIsOpen.onClose();
          handleSubmit(true);
        }}
      />

      <HStack w={{ base: 'full', md: 'auto' }} mt={1.5}>
        {remainingQuestions > 1 && <FinishEarlyButton />}
        {stage === 'ready-to-upload' && isRetryable && tryAgainBtn}
        {stage === 'error' && uploadAgainBtn}

        {(stage === 'recording' || stage === 'ready-to-record') &&
          (isRetryable ? stopRecordingBtn : submitBtn)}

        {stage === 'ready-to-upload' && submitBtn}
      </HStack>
    </Flex>
  );
};

const flash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const Loading = ({
  initialCount,
  skipCountdownToEnd,
}: UseVideoRecorderReturn) => {
  const [gray700] = useToken('colors', ['gray.700']);
  const { t } = useTranslation(['interview']);

  return (
    <Center
      position="absolute"
      display={initialCount >= 0 ? undefined : 'none'}
      zIndex="overlay"
      left={0}
      right={0}
      top={0}
      border={0}
      flex="1"
      bg={Color(gray700).alpha(0.6).string()}
      h="full"
      flexDir="column"
      gap={2}
    >
      <Text
        fontSize="small"
        color="white"
        sx={{
          animation: `${flash} 1s infinite`,
        }}
      >
        {t('interview:recordingWillStartIn', {
          time: initialCount,
        })}
      </Text>

      <Button color="brand.500" size="sm" onClick={skipCountdownToEnd}>
        {t('interview:skipCountdown')}
      </Button>
    </Center>
  );
};

const SkipQuestionModal = ({
  isOpen,
  onClose,
  onClick,
}: {
  isOpen: boolean;
  onClose: () => void;
  onClick: () => void;
}) => {
  const { t } = useTranslation(['interview', 'common']);

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('interview:videoInterview.skipQuestionTitle')}
        </ModalHeader>
        <ModalBody>
          <Text>{t('interview:videoInterview.skipQuestion')}</Text>
        </ModalBody>
        <ModalFooter>
          <HStack gap={3} w="full">
            <Button variant="@secondary" onClick={onClose} flex={1}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="@primary" onClick={onClick} w="full" flex={1}>
              {t('common:actions.skip')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
